import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Spin } from 'antd';
import { useSelector } from 'react-redux';
import type { RootState } from '@@store/index';
import { billableClick, billableFinalize } from '@/service/api';
import './index.less';

const DEFAULT_TRUSTED_ORIGINS = [
    'https://uatesupport.lenovo.com',
    'https://pcsupport.lenovo.com'
];

type PaymentReport = { status: boolean; data?: any };

export interface PaymentModalProps {
    open: boolean;
    url: string;
    onClose: () => void;
    onReport: (report: PaymentReport) => void; // eslint-disable-line no-unused-vars
    trustedOrigins?: string[];
    width?: number;
    ariaLabel?: string;
    title?: React.ReactNode;
    sessionId?: string;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
    open,
    url,
    onClose,
    onReport,
    trustedOrigins,
    width,
    ariaLabel = 'Embedded service area',
    title,
    sessionId
}) => {
    const curTheme = useSelector((state: RootState) => state.theme);
    const [ iframeLoading, setIframeLoading ] = useState<boolean>(false);
    const [ paymentData, setPaymentData ] = useState<any>(null);
    const [ paymentCompleted, setPaymentCompleted ] = useState<boolean>(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const lastReportRef = useRef<{ success: boolean; payload: any } | null>(null);
    const clickedRef = useRef<boolean>(false);
    const finalizedRef = useRef<boolean>(false);

    useEffect(() => {
        if (open && url) {
            setIframeLoading(true);
            setPaymentData(null);
            setPaymentCompleted(false);
            lastReportRef.current = null;
            clickedRef.current = false;
            finalizedRef.current = false;
            if (sessionId && !clickedRef.current) {
                clickedRef.current = true;
                billableClick(sessionId);
            }
        }
    }, [ open, url, sessionId ]);

    const allowedOrigins = useMemo(() => {
        if (trustedOrigins && trustedOrigins.length > 0) return trustedOrigins;

        const env = (process.env.REACT_APP_TRUSTED_ORIGINS || '').split(',')
            .map((s) => s.trim())
            .filter(Boolean);
        if (env.length > 0) return env;

        try {
            const origins = new Set(DEFAULT_TRUSTED_ORIGINS);
            if (url) origins.add(new URL(url).origin);
            return Array.from(origins);
        } catch {
            return DEFAULT_TRUSTED_ORIGINS;
        }
    }, [ trustedOrigins, url ]);

    useEffect(() => {
        if (!open || !url) return;

        const handleMessage = (event: MessageEvent) => {
            if (!allowedOrigins.includes(event.origin)) return;
            const frameWin = iframeRef.current?.contentWindow;
            if (!frameWin || event.source !== frameWin) return;

            let payload: any = event.data;
            try {
                if (typeof payload === 'string') payload = JSON.parse(payload);
            } catch (e) { void 0; }

            if (payload) {
                // eslint-disable-next-line no-console
                console.log('[PaymentModal postMessage]', { origin: event.origin, payload });
                const problemType = payload?.data?.params?.problemType ?? payload?.data?.problemType;
                if (problemType !== '/hardware/repair/') return;

                const status = payload?.data?.params?.status;
                const success = typeof status === 'string' ? status.toLowerCase() === 'resolved' : false;
                setPaymentCompleted(!!success);
                setPaymentData(payload);
                lastReportRef.current = { success: !!success, payload };
                if (success && sessionId && !finalizedRef.current) {
                    finalizedRef.current = true;
                    billableFinalize(sessionId);
                }
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [ open, url, allowedOrigins, sessionId ]);

    const handleClose = () => {
        const pendingReport = lastReportRef.current;
        onReport({
            status: pendingReport ? pendingReport.success : paymentCompleted,
            data: pendingReport ? pendingReport.payload : paymentData || undefined
        });
        onClose();
        setIframeLoading(false);
        setPaymentCompleted(false);
        setPaymentData(null);
        lastReportRef.current = null;
    };

    return (
        <Modal
            title={title}
            open={open}
            onCancel={handleClose}
            onOk={handleClose}
            footer={null}
            className={`bot-iframe-modal ${curTheme}-bot-iframe-modal`}
            destroyOnHidden
            width={width}
        >
            <div
                className="bot-iframe-wrapper"
                role="region"
                aria-busy={iframeLoading}
                aria-live="polite"
                aria-label={ariaLabel}
            >
                {iframeLoading && (
                    <div className={`bot-iframe-loading ${iframeLoading ? 'show' : ''}`} role="status" aria-live="polite">
                        <Spin size="large" tip="Loading…" aria-label="Loading content" />
                    </div>
                )}
                {url && (
                    <iframe
                        ref={iframeRef}
                        key={url || 'blank'}
                        src={url}
                        title="Embedded service page"
                        className="bot-iframe"
                        sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
                        onLoad={() => setIframeLoading(false)}
                    />
                )}
            </div>
        </Modal>
    );
};

export default PaymentModal;


